import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Heart, Star, Zap, Sun, Cloud, Flame, Music, Smile, ShoppingBag, Type, Save, Link2, Check, Move, RotateCcw, Upload } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import DesignPreview, { DEFAULT_GRAPHIC_POS, DEFAULT_TEXT_POS } from "@/components/DesignPreview";
import api, { formatApiError } from "@/lib/api";
import { CUSTOM_TEE_PRICE } from "@/lib/constants";

const shirtColors = [
  { name: "Yellow", hex: "#FEF08A" },
  { name: "Sky", hex: "#BAE6FD" },
  { name: "Mint", hex: "#A7F3D0" },
  { name: "Pink", hex: "#FDA4AF" },
  { name: "Lavender", hex: "#C4B5FD" },
  { name: "Peach", hex: "#FED7AA" },
  { name: "Lime", hex: "#BEF264" },
  { name: "Coral", hex: "#FB7185" },
  { name: "Grey", hex: "#94A3B8" },
  { name: "White", hex: "#FFFFFF" },
  { name: "Navy", hex: "#1E3A8A" },
  { name: "Black", hex: "#1E293B" },
];
const textColors = ["#0F172A", "#FFFFFF", "#DC2626", "#EA580C", "#FACC15", "#16A34A", "#0D9488", "#2563EB", "#7C3AED", "#DB2777"];
const graphics = { None: null, Heart, Star, Zap, Sun, Cloud, Flame, Music, Smile };
const sizes = ["XS", "S", "M", "L", "XL", "XXL", "XXXL"];
const darkShirts = ["Black", "Navy"];

export default function Designer() {
  const { addItem } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [shirt, setShirt] = useState(shirtColors[0]);
  const [text, setText] = useState("MY TEE");
  const [textColor, setTextColor] = useState("#0F172A");
  const [textSize, setTextSize] = useState([24]);
  const [graphic, setGraphic] = useState("Star");
  const [size, setSize] = useState("M");
  const [graphicPos, setGraphicPos] = useState(DEFAULT_GRAPHIC_POS);
  const [textPos, setTextPos] = useState(DEFAULT_TEXT_POS);
  const [graphicRot, setGraphicRot] = useState(0);
  const [textRot, setTextRot] = useState(0);
  const [graphicScale, setGraphicScale] = useState(1);
  const [textScale, setTextScale] = useState(1);
  const [artImage, setArtImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [designName, setDesignName] = useState("My Design");
  const [saving, setSaving] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);

  // Load a design from a share link
  useEffect(() => {
    const share = params.get("share");
    if (!share) return;
    api.get(`/designs/share/${share}`).then(({ data }) => {
      const c = data.custom || {};
      const match = shirtColors.find((s) => s.hex.toLowerCase() === (c.shirtColor || "").toLowerCase());
      if (match) setShirt(match);
      if (c.text !== undefined) setText(c.text);
      if (c.textColor) setTextColor(c.textColor);
      if (c.textSize) setTextSize([c.textSize]);
      if (c.graphic) setGraphic(c.graphic);
      if (c.graphicPos) setGraphicPos(c.graphicPos);
      if (c.textPos) setTextPos(c.textPos);
      if (c.graphicRot !== undefined) setGraphicRot(c.graphicRot);
      if (c.textRot !== undefined) setTextRot(c.textRot);
      if (c.graphicScale !== undefined) setGraphicScale(c.graphicScale);
      if (c.textScale !== undefined) setTextScale(c.textScale);
      if (c.image) setArtImage(c.image);
      if (data.size) setSize(data.size);
      if (data.name) setDesignName(data.name);
      toast.success("Design loaded", { description: "Tweak it and make it yours." });
    }).catch(() => {});
  }, [params]);

  const buildCustom = () => ({
    shirtColor: shirt.hex, shirtColorName: shirt.name, text, textColor, textSize: textSize[0], graphic,
    graphicPos, textPos, graphicRot, textRot, graphicScale, textScale, image: artImage || null,
  });

  const onUpload = async (e) => {    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Image too large (max 5MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setArtImage(data.url);
      setGraphic("None");
      toast.success("Image uploaded!", { description: "Drag, rotate or resize it on the tee." });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDesignChange = (patch) => {
    if (patch.graphicPos) setGraphicPos(patch.graphicPos);
    if (patch.textPos) setTextPos(patch.textPos);
    if (patch.graphicRot !== undefined) setGraphicRot(patch.graphicRot);
    if (patch.textRot !== undefined) setTextRot(patch.textRot);
    if (patch.graphicScale !== undefined) setGraphicScale(patch.graphicScale);
    if (patch.textScale !== undefined) setTextScale(patch.textScale);
  };

  const resetLayout = () => {
    setGraphicPos(DEFAULT_GRAPHIC_POS);
    setTextPos(DEFAULT_TEXT_POS);
    setGraphicRot(0);
    setTextRot(0);
    setGraphicScale(1);
    setTextScale(1);
  };

  const handleAdd = () => {
    addItem({
      key: `custom-${Date.now()}`, product_id: "custom", name: "Custom Designed Tee",
      image: null, price: CUSTOM_TEE_PRICE, size, color: shirt.name, quantity: 1, custom: buildCustom(),
    });
    toast.success("Custom tee added!", { description: `${shirt.name} · ${size} · $${CUSTOM_TEE_PRICE}` });
  };

  const handleSave = async () => {
    if (!user) {
      toast("Sign in to save designs", { description: "Create an account to save & share." });
      navigate("/login");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/designs", { name: text || "My Design", custom: buildCustom(), size });
      const url = `${window.location.origin}/design/${data.share_id}`;
      setShareUrl(url);
      toast.success("Design saved!", { description: "Find it in your account. Share link ready below." });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const copyShare = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const custom = buildCustom();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="designer-page">
      <div className="mb-8">
        <span className="inline-block bg-brand-mint neo-sm rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-widest mb-3">Design Studio</span>
        <h1 className="font-display font-bold text-4xl sm:text-5xl">Fabricate your own</h1>
        <p className="mt-2 font-semibold text-ink/70">Colors, words, art. Build it live, save it, share it.</p>
      </div>

      <div className="grid gap-8 lg:grid-cols-2">
        <div className="bg-brand-sky/40 neo rounded-3xl p-8 flex flex-col items-center sticky top-24 self-start" data-testid="tee-preview">
          <DesignPreview custom={custom} className="w-full max-w-sm" editable onChange={handleDesignChange} />
          <div className="mt-4 flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 bg-white neo-sm rounded-full px-3 py-1.5 text-xs font-bold" data-testid="drag-hint">
              <Move className="w-3.5 h-3.5" /> Drag to move · tap, then rotate / resize
            </span>
            <button onClick={resetLayout} className="inline-flex items-center gap-1.5 bg-white neo-sm rounded-full px-3 py-1.5 text-xs font-bold neo-press" data-testid="reset-layout-btn">
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
          </div>
        </div>

        <div className="bg-white neo rounded-3xl p-6 sm:p-8">
          <Tabs defaultValue="color">
            <TabsList className="grid grid-cols-3 w-full bg-brand-yellow border-2 border-ink rounded-xl p-1 h-auto">
              <TabsTrigger value="color" data-testid="tab-color" className="rounded-lg font-bold data-[state=active]:bg-ink data-[state=active]:text-paper">Color</TabsTrigger>
              <TabsTrigger value="text" data-testid="tab-text" className="rounded-lg font-bold data-[state=active]:bg-ink data-[state=active]:text-paper">Text</TabsTrigger>
              <TabsTrigger value="graphic" data-testid="tab-graphic" className="rounded-lg font-bold data-[state=active]:bg-ink data-[state=active]:text-paper">Art</TabsTrigger>
            </TabsList>

            <TabsContent value="color" className="mt-6">
              <p className="text-xs font-bold uppercase tracking-widest mb-3">Shirt color</p>
              <div className="grid grid-cols-3 gap-3">
                {shirtColors.map((c) => (
                  <button key={c.name} onClick={() => { setShirt(c); if (darkShirts.includes(c.name) && textColor === "#0F172A") setTextColor("#FFFFFF"); }} data-testid={`shirt-color-${c.name}`}
                    className={`flex items-center gap-2 p-3 rounded-xl border-2 border-ink neo-press font-bold ${shirt.name === c.name ? "ring-2 ring-brand-pink" : ""}`}>
                    <span className="w-6 h-6 rounded-full border-2 border-ink" style={{ background: c.hex }} />{c.name}
                  </button>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="text" className="mt-6 space-y-6">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest mb-2">Your text</p>
                <div className="flex items-center gap-2 border-2 border-ink rounded-xl px-3">
                  <Type className="w-4 h-4 shrink-0" />
                  <input value={text} onChange={(e) => setText(e.target.value.slice(0, 22))} maxLength={22}
                    placeholder="Type something bold" data-testid="text-input" className="w-full py-3 outline-none font-bold bg-transparent" />
                </div>
                <p className="text-xs font-semibold text-ink/50 mt-1">{text.length}/22</p>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest mb-2">Text size</p>
                <Slider min={14} max={40} step={1} value={textSize} onValueChange={setTextSize} data-testid="text-size-slider" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest mb-3">Text color</p>
                <div className="flex flex-wrap gap-3">
                  {textColors.map((c) => (
                    <button key={c} onClick={() => setTextColor(c)} data-testid={`text-color-${c}`}
                      className={`w-10 h-10 rounded-full border-2 border-ink neo-press ${textColor === c ? "ring-2 ring-offset-2 ring-brand-pink" : ""}`} style={{ background: c }} />
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="graphic" className="mt-6">
              <p className="text-xs font-bold uppercase tracking-widest mb-3">Pick some art</p>
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(graphics).map(([name, Icon]) => (
                  <button key={name} onClick={() => { setGraphic(name); setArtImage(null); }} data-testid={`graphic-${name}`}
                    className={`grid place-items-center gap-1 p-4 rounded-xl border-2 border-ink neo-press ${!artImage && graphic === name ? "bg-brand-mint" : "bg-white"}`}>
                    {Icon ? <Icon className="w-7 h-7" /> : <span className="w-7 h-7 grid place-items-center font-bold">∅</span>}
                    <span className="text-xs font-bold">{name}</span>
                  </button>
                ))}
              </div>

              <div className="mt-6 pt-6 border-t-2 border-ink">
                <p className="text-xs font-bold uppercase tracking-widest mb-3">Or upload your own logo / image</p>
                <input ref={fileInputRef} type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="art-upload-input" />
                {artImage ? (
                  <div className="flex items-center gap-3 flex-wrap">
                    <img src={artImage} alt="uploaded art" className="w-16 h-16 object-contain border-2 border-ink rounded-lg bg-white p-1" data-testid="uploaded-art-thumb" />
                    <button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 bg-brand-sky font-bold px-4 py-2.5 rounded-lg neo-press border-2 border-ink" data-testid="replace-art-btn">
                      <Upload className="w-4 h-4" /> Replace
                    </button>
                    <button onClick={() => setArtImage(null)} className="bg-brand-pink font-bold px-4 py-2.5 rounded-lg neo-press border-2 border-ink" data-testid="remove-art-btn">Remove</button>
                  </div>
                ) : (
                  <button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 bg-brand-sky font-bold px-5 py-3 rounded-xl neo-press border-2 border-ink disabled:opacity-60" data-testid="art-upload-btn">
                    <Upload className="w-5 h-5" /> {uploading ? "Uploading…" : "Upload image"}
                  </button>
                )}
                <p className="text-xs font-semibold text-ink/50 mt-2">PNG, JPG or WEBP · max 5MB. Drag, rotate &amp; resize it on the tee.</p>
              </div>
            </TabsContent>
          </Tabs>

          <div className="mt-8 pt-6 border-t-2 border-ink">
            <p className="text-xs font-bold uppercase tracking-widest mb-3">Size</p>
            <div className="flex flex-wrap gap-3 mb-6">
              {sizes.map((s) => (
                <button key={s} onClick={() => setSize(s)} data-testid={`custom-size-${s}`}
                  className={`min-w-[3rem] h-12 px-2 rounded-lg font-bold border-2 border-ink neo-press ${size === s ? "bg-ink text-paper" : "bg-white"}`}>{s}</button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <span className="font-display font-bold text-3xl bg-brand-yellow neo-sm rounded-lg px-3 py-1">${CUSTOM_TEE_PRICE}</span>
              <button onClick={handleSave} disabled={saving} className="inline-flex items-center justify-center gap-2 bg-brand-sky font-bold px-5 py-3.5 rounded-xl neo-press border-2 border-ink disabled:opacity-60" data-testid="save-design-btn">
                <Save className="w-5 h-5" /> {saving ? "Saving…" : "Save"}
              </button>
              <button onClick={handleAdd} className="flex-1 min-w-[160px] inline-flex items-center justify-center gap-2 bg-ink text-paper font-bold px-6 py-3.5 rounded-xl neo-press border-2 border-ink" data-testid="custom-add-to-cart-btn">
                <ShoppingBag className="w-5 h-5" /> Add to Cart
              </button>
            </div>

            {shareUrl && (
              <div className="mt-5 bg-brand-mint neo rounded-xl p-4" data-testid="share-box">
                <p className="text-xs font-bold uppercase tracking-widest mb-2 flex items-center gap-2"><Link2 className="w-4 h-4" /> Share link</p>
                <div className="flex items-center gap-2">
                  <input readOnly value={shareUrl} className="flex-1 bg-white border-2 border-ink rounded-lg px-3 py-2 text-sm font-semibold" data-testid="share-url" />
                  <button onClick={copyShare} className="bg-ink text-paper font-bold px-4 py-2 rounded-lg neo-press border-2 border-ink inline-flex items-center gap-1" data-testid="copy-share-btn">
                    {copied ? <><Check className="w-4 h-4" /> Copied</> : "Copy"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
